import React, { useState, useEffect, useRef, useMemo } from 'react';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { denormalisedResponseEntities, ensureCurrentUser, ensureListing } from '../../../util/data';
import { createImageVariantConfig } from '../../../util/sdkLoader';
import { isScrollingDisabled, manageDisableScrolling } from '../../../ducks/ui.duck';
import { H3, Modal, Page, LayoutSingleColumn, PrimaryButton } from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../util/routes';
import { createSlug } from '../../../util/urlHelpers';
import { deriveCityFromAddress } from '../../../util/linkupData';
import { getStateCode } from '../../../util/stateNames';
import { TICKETMASTER_MARKETS } from '../../../util/ticketmasterMarkets';
import { LISTING_TYPES, PRICE_VERIFICATION_STATUSES } from '../../../util/listingTypes';

import TopbarContainer from '../../TopbarContainer/TopbarContainer';
import FooterContainer from '../../FooterContainer/FooterContainer';
import { mockCrews } from './constants';
import HeroSearchSection from './components/HeroSearchSection';
import EventsSection from './components/EventsSection';
import CrewsSection from './components/CrewsSection';
import ListingsSection from './components/ListingsSection';
import LinkUpCard from '../../CrewsPage/components/LinkUpCard/LinkUpCard';
import BidCard from '../../BidsPage/components/BidCard';
import ScrollControls from '../../../components/ScrollControls/ScrollControls';
import useEmailVerification from './hooks/useEmailVerification';
import useNavigationHandlers from './hooks/useNavigationHandlers';
import useScrollRefs from './hooks/useScrollRefs';
import { getLandingViewerKey, hasLandingViewerChanged } from './utils/viewerSession';
import {
  getCsvDiscoveryEvents,
  getCsvBatchDiscoveryEvents,
  getUserPreferences,
  resolveBidListing as resolveBidListingApi,
} from '../../../util/api';
import { fetchCrewHighlights } from '../../../ducks/crewHighlights.duck';
import {
  cancelPersonalizedLandingEventFeed,
  fetchLandingEventsBatch,
  fetchPersonalizedLandingEventFeed,
  fetchPublicLandingEventFeed,
} from '../../../ducks/landingEvents.duck';
import { fetchUserCrews, fetchFeaturedCrews } from '../../../ducks/crews.duck';
import { joinLinkUp, fetchLinkups } from '../../../ducks/linkups.duck';
import {
  fetchBids as fetchBrowseBids,
  getBidsResultIds,
  isFetchingBids as isFetchingBrowseBids,
} from '../../BidsBrowsePage/BidsBrowsePage.duck';
import { getMarketplaceEntities } from '../../../ducks/marketplaceData.duck';
import {
  selectHighlightsByCrewId,
  selectCrewHighlightsError,
  selectCrewHighlightsLoading,
} from '../../../selectors/crewHighlights';
import {
  selectCrewsLoading,
  selectFormattedFeaturedCrews,
  selectRawUserCrews,
} from '../../../selectors/crews';
import {
  selectJoinedLinkUpIds,
  selectLinkupsForCards,
  selectLinkupsLoading,
  selectRawLinkups,
} from '../../../selectors/linkups';
import {
  selectAuthenticatedLandingHasPreferences,
  selectAuthenticatedLandingRecommendations,
  selectAuthenticatedLandingRecommendationsError,
  selectAuthenticatedLandingRecommendationsLoading,
} from '../../../selectors/eventRecommendations';
import SectionHeader from './components/SectionHeader';
import verifiedIcon from '../../../assets/verified.svg';
import crewsIcon from '../../../assets/linkup.svg';
import linkupsIcon from '../../../assets/linkup.svg';
import addIcon from '../../../assets/add.svg';
import locationIcon from '../../../assets/locationpin.svg';
import bidsIcon from '../../../assets/bidshand.svg';
import musicIcon from '../../../assets/music.svg';
import soccerIcon from '../../../assets/soccer.png';
import maskIcon from '../../../assets/mask.svg';
import smileIcon from '../../../assets/smile.svg';
import highlightsIcon from '../../../assets/highlights.svg';
import recommendationsIcon from '../../../assets/Recommendations_icon.svg';

import css from './CustomLandingPage.module.css';
import checkoutCss from '../../CheckoutPage/CheckoutPage.module.css';

const CrewHighlightsSection = loadable(() =>
  import(
    /* webpackChunkName: "CrewHighlightsSection" */ '../../../components/CrewHighlightsSection/CrewHighlightsSection'
  )
);
const LinkUpDetailsModal = loadable(() =>
  import(/* webpackChunkName: "LandingLinkUpDetailsModal" */ '../../CrewsPage/LinkUpDetailsModal')
);

const LANDING_FEATURED_CREWS_LIMIT = 24;
const LANDING_LINKUPS_LIMIT = 12;
const LANDING_EVENTS_CACHE_TTL = 15 * 60 * 1000;
const LANDING_SECTION_MIN_ITEMS = 5;
const LANDING_SECTION_EVENT_LIMIT = 8;
const USE_OPTIMIZED_LANDING_EVENT_FEEDS =
  process.env.REACT_APP_USE_OPTIMIZED_LANDING_EVENT_FEEDS !== 'false';
// The batch endpoint currently returns empty section payloads even when the
// individual discovery endpoint has matching events. Keep the optimized,
// cached individual flow active until batching can preserve result parity.
const USE_BATCH_LANDING_EVENTS = false;
const LANDING_SECTION_LISTING_LIMIT = 10;
const LANDING_SECTION_BIDS_LIMIT = 10;
const LANDING_SECTION_DISPLAY_LIMIT = 10;
const LANDING_GUEST_EVENTS_PER_CATEGORY = 2;
const CATEGORY_SECTION_CONFIG = [
  { key: 'music', title: 'Music', classificationName: 'Music', icon: musicIcon },
  { key: 'sports', title: 'Sports', classificationName: 'Sports', icon: soccerIcon },
  { key: 'comedy', title: 'Comedy', classificationName: 'Comedy', icon: smileIcon },
  { key: 'theater', title: 'Theater', classificationName: 'Arts & Theatre', icon: maskIcon },
];
const CATEGORY_COLORS = {
  music: '#7b2cbf',
  sports: '#2b7abf',
  theater: '#bf2b7a',
  comedy: '#bfaf2b',
};
const TICKETMASTER_PREFERRED_IMAGE_RATIOS = ['16_9', '3_2', '4_3'];
const MIN_TICKETMASTER_IMAGE_WIDTH = 500;
const MIN_TICKETMASTER_IMAGE_HEIGHT = 280;

const categoryEventsCache = {
  data: null,
  fetchedAt: 0,
};

const nearYouEventsCache = new Map();
const eventsForYouCache = new Map();

const meetsMinimumSectionItems = items =>
  Array.isArray(items) && items.length >= LANDING_SECTION_MIN_ITEMS;

const extractTicketBidDate = publicData => {
  if (publicData.event_date) {
    return publicData.event_date;
  }

  const isoDateTime = publicData.eventDateTime;
  if (typeof isoDateTime !== 'string') {
    return '';
  }

  if (isoDateTime.includes('T')) {
    return isoDateTime.split('T')[0];
  }

  return isoDateTime.match(/^\d{4}-\d{2}-\d{2}/) ? isoDateTime.substring(0, 10) : '';
};

const getTicketBidImageUrl = listing => {
  const firstImage = listing.images?.[0];
  if (!firstImage) {
    return '';
  }

  return (
    firstImage.attributes?.variants?.['listing-card']?.url ||
    firstImage.attributes?.variants?.['scaled-medium']?.url ||
    firstImage.attributes?.variants?.['scaled-small']?.url ||
    ''
  );
};

const extractTicketBidMessage = publicData => {
  const possibleKeys = [
    'message',
    'buyersMessage',
    'buyers_message',
    'Buyers message',
    'Buyer message',
    "Buyer's message",
    'buyerMessage',
    'buyer_message',
  ];

  const directMessage = possibleKeys
    .map(key => publicData[key])
    .find(value => typeof value === 'string' && value.trim().length > 0);

  if (directMessage) {
    return directMessage.trim();
  }

  const messageKey = Object.keys(publicData || {}).find(
    key =>
      typeof key === 'string' &&
      key.toLowerCase().includes('message') &&
      typeof publicData[key] === 'string' &&
      publicData[key].trim().length > 0
  );

  return messageKey ? publicData[messageKey].trim() : null;
};

const formatBrowseListingForBidCard = listing => {
  const attrs = listing.attributes || {};
  const publicData = attrs.publicData || {};
  const listingId = listing.id?.uuid || listing.id?.id?.uuid || listing.id || '';
  const isBidMarkedActive = publicData.active !== false;
  const derivedStatus = !isBidMarkedActive
    ? 'closed'
    : attrs.state === 'published'
    ? 'active'
    : attrs.state || 'active';

  return {
    id: listingId,
    title: attrs.title || '',
    date: extractTicketBidDate(publicData),
    venue: publicData.venue || '',
    location: publicData.event_location || '',
    numberOfTickets: publicData.number_of_tickets || publicData.numberOfTickets || 1,
    currentBid: (publicData.min_price || 0) / 100,
    maxBid: (publicData.max_price || 0) / 100,
    image: getTicketBidImageUrl(listing),
    description: attrs.description || '',
    buyersMessage: extractTicketBidMessage(publicData),
    status: derivedStatus,
    eventType: publicData.ticket_type || publicData.ticketType || '',
    listing,
  };
};

const hasSavedInterestSelections = preferences => {
  const selectedMainCategories = preferences?.interests?.selected_main_categories;
  return Array.isArray(selectedMainCategories) && selectedMainCategories.length > 0;
};

const extractInterestTerms = preferences => {
  const interests = preferences?.interests || {};
  const mainCategories = Array.isArray(interests.selected_main_categories)
    ? interests.selected_main_categories
    : [];
  const selectedSubcategories =
    interests.selected_subcategories && typeof interests.selected_subcategories === 'object'
      ? interests.selected_subcategories
      : {};

  const subcategories = Object.values(selectedSubcategories).flatMap(value =>
    Array.isArray(value) ? value : []
  );

  return [...new Set([...mainCategories, ...subcategories].map(String).filter(Boolean))];
};

const hasEvents = response => Array.isArray(response?.events) && response.events.length > 0;
const getCsvDiscoveryEventsSafely = params =>
  getCsvDiscoveryEvents(params).catch(error => {
    console.warn('[CustomLandingPage] CSV discovery fallback after request failed:', {
      params,
      message: error?.message || 'unknown error',
    });
    return { events: [], count: 0, source: 'csv_events' };
  });

const getCategoryColor = category => CATEGORY_COLORS[category] || '#6b7280';

const formatCityStateLocation = (city, state) => {
  const cityText = typeof city === 'string' ? city.trim() : '';
  const stateText = typeof state === 'string' ? state.trim().toUpperCase() : '';

  if (cityText && stateText) {
    return `${cityText}, ${getStateCode(stateText) || stateText}`;
  }

  return cityText || stateText || 'Location TBD';
};

const formatWholeDollarPrice = amount => `$${Math.round(amount).toLocaleString('en-US')}`;

const formatEventPrice = event => {
  const priceRange = Array.isArray(event?.priceRanges) ? event.priceRanges[0] : null;
  const minPrice = Number(priceRange?.min);

  if (Number.isFinite(minPrice) && minPrice > 0) {
    return `From ${formatWholeDollarPrice(minPrice)}`;
  }

  return event?.price || null;
};

const pickBestTicketmasterImage = (images = []) => {
  const validImages = images.filter(image => {
    const width = Number(image?.width);
    const height = Number(image?.height);

    return Boolean(image?.url) && Number.isFinite(width) && Number.isFinite(height);
  });

  if (!validImages.length) {
    return images.find(image => image?.url)?.url || null;
  }

  const sortByQuality = (a, b) => {
    const aArea = Number(a.width) * Number(a.height);
    const bArea = Number(b.width) * Number(b.height);

    if (bArea !== aArea) {
      return bArea - aArea;
    }

    return Number(b.width) - Number(a.width);
  };

  const highQualityPreferred = TICKETMASTER_PREFERRED_IMAGE_RATIOS.flatMap(ratio =>
    validImages
      .filter(
        image =>
          image.ratio === ratio &&
          Number(image.width) >= MIN_TICKETMASTER_IMAGE_WIDTH &&
          Number(image.height) >= MIN_TICKETMASTER_IMAGE_HEIGHT
      )
      .sort(sortByQuality)
  )[0];

  if (highQualityPreferred) {
    return highQualityPreferred.url;
  }

  const largestHighQuality = validImages
    .filter(
      image =>
        Number(image.width) >= MIN_TICKETMASTER_IMAGE_WIDTH &&
        Number(image.height) >= MIN_TICKETMASTER_IMAGE_HEIGHT
    )
    .sort(sortByQuality)[0];

  if (largestHighQuality) {
    return largestHighQuality.url;
  }

  const largestOverall = [...validImages].sort(sortByQuality)[0];
  return largestOverall?.url || null;
};

const hasFreshLandingCache = fetchedAt => Date.now() - fetchedAt < LANDING_EVENTS_CACHE_TTL;

const scheduleDeferredLandingTask = (callback, options = {}) => {
  const { timeout = 500, delay = 120 } = options;

  if (typeof window === 'undefined') {
    callback();
    return () => {};
  }

  if (typeof window.requestIdleCallback === 'function') {
    const idleId = window.requestIdleCallback(() => callback(), { timeout });
    return () => window.cancelIdleCallback(idleId);
  }

  const timeoutId = window.setTimeout(callback, delay);
  return () => window.clearTimeout(timeoutId);
};

const observeLandingSection = (element, onVisible) => {
  if (typeof window === 'undefined' || !element) {
    onVisible();
    return () => {};
  }

  if (typeof window.IntersectionObserver !== 'function') {
    onVisible();
    return () => {};
  }

  const observer = new window.IntersectionObserver(
    entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        onVisible();
        observer.disconnect();
      }
    },
    { rootMargin: '240px 0px' }
  );

  observer.observe(element);
  return () => observer.disconnect();
};

const formatLandingEventForCard = (event, matchReasons = []) => {
  let eventDate = 'Date TBA';
  let eventTime = null;

  if (event.eventDate?.fullDate) {
    try {
      const parsedDate = new Date(event.eventDate.fullDate);
      if (!isNaN(parsedDate.getTime())) {
        const month = parsedDate.toLocaleDateString('en-US', { month: 'short' });
        const day = parsedDate.getDate();
        eventDate = `${month} ${day}`;

        const hours = parsedDate.getHours();
        const minutes = parsedDate.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, '0');
        eventTime = `${displayHours}:${displayMinutes} ${ampm}`;
      }
    } catch (e) {}
  }

  if (eventDate === 'Date TBA' && event.eventDate && event.eventDate.month && event.eventDate.day) {
    eventDate = `${event.eventDate.month} ${event.eventDate.day}`;
  }

  if (eventDate === 'Date TBA' && event.date) {
    try {
      const parsedDate = new Date(event.date);
      if (!isNaN(parsedDate.getTime())) {
        const month = parsedDate.toLocaleDateString('en-US', { month: 'short' });
        const day = parsedDate.getDate();
        eventDate = `${month} ${day}`;
      }
    } catch (e) {}
  }

  const venue = event.location?.name || 'Venue TBA';
  const city = event.location?.city || '';
  const state = event.location?.stateCode || '';
  const locationStr = `${venue}${city ? ` • ${city}${state ? ', ' + state : ''}` : ''}`;
  const locationLabel = formatCityStateLocation(city, state);
  const image =
    pickBestTicketmasterImage(event.images || []) ||
    event.image ||
    event.image_url ||
    event.event_image ||
    event.attractionImageUrl ||
    event.attraction_image_url ||
    null;

  return {
    id: event.id,
    ticketmasterId: event.ticketmasterId || event.id,
    title: event.name,
    venue: locationStr,
    locationLabel,
    date: eventDate,
    time: eventTime,
    price: formatEventPrice(event),
    category: event.category || 'event',
    crew: matchReasons[0] || 'Recommended',
    color: getCategoryColor(event.category),
    members: '',
    image,
    ticketmasterUrl: event.url,
    matchReasons,
    score: event.score,
    isSupabaseEvent: !!event.isSupabaseEvent,
    attractionName: event.attractionName || null,
    event_name: event.event_name || event.name,
    venue_name: event.venue_name || event.location?.name || null,
    venue_city: event.venue_city || event.location?.city || null,
    venue_state: event.venue_state || event.location?.stateCode || event.location?.state || null,
    event_date: event.event_date || event.date || event.eventDate?.fullDate || null,
    event_time: event.event_time || null,
  };
};

const fetchVerifiedListings = (params = {}) => (dispatch, getState, sdk) => {
  const { limit = LANDING_SECTION_LISTING_LIMIT } = params;
  const queryLimit = Math.min(limit * 3, 100);
  const aspectRatio = 1;

  if (!sdk) {
    return Promise.resolve([]);
  }

  return sdk.listings
    .query({
      include: ['author', 'author.profileImage', 'images'],
      'fields.listing': ['title', 'description', 'price', 'publicData', 'metadata'],
      'fields.image': [
        'variants.listing-card',
        'variants.listing-card-2x',
        'variants.scaled-medium',
        'variants.scaled-small',
      ],
      ...createImageVariantConfig('listing-card', 400, aspectRatio),
      ...createImageVariantConfig('listing-card-2x', 800, aspectRatio),
      per_page: queryLimit,
      states: ['published'],
      pub_listingType: 'buying-tickets',
      pub_transactionProcessAlias: 'default-purchase/release-1',
      minStock: 1,
    })
    .then(response => denormalisedResponseEntities(response))
    .then(listings =>
      listings
        .filter(listing => {
          const publicData = listing?.attributes?.publicData || {};
          const source = String(publicData.source || '').toLowerCase();
          const priceVerificationStatus = publicData.price_verification_status;
          return (
            source !== 'ticketmaster' &&
            source !== 'seatgeek' &&
            priceVerificationStatus === PRICE_VERIFICATION_STATUSES.VERIFIED
          );
        })
        .slice(0, limit)
    )
    .catch(error => {
      console.error('[CustomLandingPage] Failed to fetch verified listings:', error);
      return [];
    });
};

// Helper function to map Sharetribe listing to ListingCard format
const mapSharetribeListingToCard = listing => {
  const attributes = listing?.attributes || {};
  const publicData = attributes.publicData || {};
  const seatSection = publicData.seat_section || publicData.seatSection;
  const seatRow = publicData.seat_row || publicData.seatRow;
  const seatNumber = publicData.seat_number || publicData.seatNumber;
  const seatParts = [
    seatSection ? `Section ${seatSection}` : null,
    seatRow ? `Row ${seatRow}` : null,
    seatNumber ? `Seat ${seatNumber}` : null,
  ].filter(Boolean);
  const cityState = [publicData.city, publicData.state]
    .map(value => (value ? String(value).trim() : ''))
    .filter(Boolean)
    .join(', ');

  const toDisplayAmount = money =>
    typeof money?.amount === 'number' && !Number.isNaN(money.amount) ? money.amount / 100 : 0;

  const getListingImageUrl = listing => {
    const image = listing?.images?.[0];
    const variants = image?.attributes?.variants || {};

    return (
      variants['listing-card-2x']?.url ||
      variants['listing-card']?.url ||
      variants['scaled-large']?.url ||
      variants['scaled-medium']?.url ||
      variants.default?.url ||
      null
    );
  };

  const getListingStatus = listing => {
    const attributes = listing?.attributes || {};
    const publicData = attributes.publicData || {};
    const state = attributes.state;
    const currentStock = listing?.currentStock?.attributes?.quantity;
    const eventDateTime =
      publicData.eventDateTime ||
      publicData.event_date_time ||
      publicData.event_date ||
      publicData.eventDate ||
      null;

    if (state === 'closed') {
      return 'archived';
    }

    if (state === 'pendingApproval' || state === 'pending-approval' || currentStock === 0) {
      return 'pending-sales';
    }

    if (eventDateTime) {
      const parsedDate = new Date(eventDateTime);
      if (!Number.isNaN(parsedDate.getTime()) && parsedDate < new Date()) {
        return 'event-passed';
      }
    }

    return 'active';
  };

  return {
    id: listing?.id?.uuid || listing?.id,
    slug: createSlug(attributes.title || publicData.eventName || 'listing'),
    title: attributes.title || publicData.eventName || 'Untitled listing',
    price: toDisplayAmount(attributes.price),
    venue: publicData.venueName || publicData.venue || 'Venue TBA',
    location:
      publicData.venueLocation ||
      publicData.location ||
      publicData.event_location ||
      cityState ||
      'Location TBA',
    date:
      publicData.event_date ||
      publicData.eventDate ||
      (publicData.eventDateTime ? publicData.eventDateTime.split('T')[0] : null),
    time: publicData.event_time || publicData.eventTime || null,
    description: seatParts.join(', ') || attributes.description || '',
    image: getListingImageUrl(listing),
    status: getListingStatus(listing),
    listingType: publicData.listingType || null,
    priceVerificationStatus: publicData.price_verification_status || null,
  };
};

const CustomLandingPageComponent = ({
  currentUser = null,
  scrollingDisabled,
  onManageDisableScrolling,
  history,
  isAuthenticated = false,
  recommendationData = null,
  recommendationsLoading = false,
  recommendationsError = null,
  hasRecommendationPreferences = true,
  // Redux state
  userCrews = [],
  bidListings = [],
  browseBidsLoading = false,
  highlightsByCrewId = {},
  fetchInProgress = false,
  fetchError = null,
  // Logged-out data from Redux
  featuredCrews = [],
  isFetchingCrewsData = false,
  allLinkups = [],
  transformedLinkups = [],
  joinedLinkUpIds = [],
  isFetchingLinkupsData = false,
  // Redux actions
  onFetchUserCrews,
  onFetchCrewHighlights,
  onFetchFeaturedCrews,
  onFetchLinkups,
  onJoinLinkUp,
  onFetchBrowseBids,
  onFetchVerifiedListings,
  onFetchLandingEventsBatch,
  onFetchPublicLandingEventFeed,
  onFetchPersonalizedLandingEventFeed,
  onCancelPersonalizedLandingEventFeed,
  landingEventSections,
  seoMetaProps = {},
}) => {
  const intl = useIntl();
  const user = ensureCurrentUser(currentUser);
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  // LinkUp modal state (for crew highlights LinkUps)
  const [showLinkUpModal, setShowLinkUpModal] = useState(false);
  const [selectedLinkUp, setSelectedLinkUp] = useState(null);
  const [showPaymentFailedPreview, setShowPaymentFailedPreview] = useState(false);

  const [categoryEvents, setCategoryEvents] = useState({
    music: [],
    sports: [],
    comedy: [],
    theater: [],
  });
  const [isLoadingCategoryEvents, setIsLoadingCategoryEvents] = useState(false);
  const [loadingCategoryEventsByKey, setLoadingCategoryEventsByKey] = useState({
    music: false,
    sports: false,
    comedy: false,
    theater: false,
  });
  const [nearYouEvents, setNearYouEvents] = useState([]);
  const [isLoadingNearYouEvents, setIsLoadingNearYouEvents] = useState(false);
  const [verifiedListings, setVerifiedListings] = useState([]);
  const [isLoadingVerifiedListings, setIsLoadingVerifiedListings] = useState(false);
  const [locationPreferences, setLocationPreferences] = useState(null);
  const [userHasSavedInterests, setUserHasSavedInterests] = useState(null);
  const [userInterestTerms, setUserInterestTerms] = useState([]);
  const [csvEventsForYou, setCsvEventsForYou] = useState([]);
  const [isLoadingCsvEventsForYou, setIsLoadingCsvEventsForYou] = useState(false);
  const [deferredLandingReady, setDeferredLandingReady] = useState(false);
  const [secondaryLandingReady, setSecondaryLandingReady] = useState(false);
  const [heavyLandingReady, setHeavyLandingReady] = useState(false);
  const [showCrewHighlightsSection, setShowCrewHighlightsSection] = useState(false);
  const [showLoggedOutLinkUpsSection, setShowLoggedOutLinkUpsSection] = useState(false);
  const hasBootstrappedGuestData = useRef(false);
  const hasBootstrappedAuthenticatedData = useRef(false);
  const activeViewerKeyRef = useRef(null);
  // Ref for logged-out linkups scroll
  const loggedOutLinkUpsRef = useRef(null);
  const crewHighlightsSectionRef = useRef(null);
  const loggedOutLinkUpsSectionRef = useRef(null);
  const eventsNearYouRef = useRef(null);
  const verifiedListingsRef = useRef(null);
  const ticketBidsRef = useRef(null);
  const discoverCrewsRef = useRef(null);
  const categorySectionRefs = useRef({
    music: React.createRef(),
    sports: React.createRef(),
    comedy: React.createRef(),
    theater: React.createRef(),
  });

  // Scroll refs hook
  const { eventsForYouRef, crewHighlightsRef, crewsRef } = useScrollRefs();

  // Email verification hook
  const { checkEmailVerification } = useEmailVerification(user, isAuthenticated);

  // Navigation handlers hook
  const {
    handleSearch,
    handleEventClick,
    handleAttractionClick,
    handleCrewClick,
    handleJoinCrew,
  } = useNavigationHandlers(checkEmailVerification);

  // The landing page can stay mounted while a user logs out and another user logs in.
  // Reset all viewer-specific bootstrap flags and derived preference state so the new
  // session always reloads its own interests, location, crews, and recommendations.
  useEffect(() => {
    const nextViewerKey = getLandingViewerKey(isAuthenticated, currentUser?.id?.uuid);

    if (!hasLandingViewerChanged(activeViewerKeyRef.current, nextViewerKey)) {
      return;
    }

    activeViewerKeyRef.current = nextViewerKey;
    hasBootstrappedAuthenticatedData.current = false;
    hasBootstrappedGuestData.current = false;
    setLocationPreferences(null);
    setUserHasSavedInterests(null);
    setUserInterestTerms([]);
    setCsvEventsForYou([]);
    setNearYouEvents([]);
    setIsLoadingNearYouEvents(false);
  }, [currentUser?.id?.uuid, isAuthenticated]);

  useEffect(() => {
    if (!USE_OPTIMIZED_LANDING_EVENT_FEEDS || !onFetchPublicLandingEventFeed) {
      return undefined;
    }

    let didCancel = false;
    setIsLoadingCategoryEvents(true);
    setLoadingCategoryEventsByKey({
      music: true,
      sports: true,
      comedy: true,
      theater: true,
    });

    onFetchPublicLandingEventFeed()
      .then(payload => {
        if (didCancel) return;
        const sections = payload?.sections || {};
        const nextCategoryEvents = CATEGORY_SECTION_CONFIG.reduce(
          (result, section) => ({
            ...result,
            [section.key]: Array.isArray(sections[section.key]?.events)
              ? sections[section.key].events.map(event =>
                  formatLandingEventForCard(event, [section.title])
                )
              : [],
          }),
          { music: [], sports: [], comedy: [], theater: [] }
        );

        setCategoryEvents(nextCategoryEvents);
        setIsLoadingCategoryEvents(false);
        setLoadingCategoryEventsByKey({
          music: false,
          sports: false,
          comedy: false,
          theater: false,
        });
      })
      .catch(error => {
        if (didCancel) return;
        console.error('[CustomLandingPage] Failed to fetch public landing event feed:', error);
        setCategoryEvents({ music: [], sports: [], comedy: [], theater: [] });
        setIsLoadingCategoryEvents(false);
        setLoadingCategoryEventsByKey({
          music: false,
          sports: false,
          comedy: false,
          theater: false,
        });
      });

    return () => {
      didCancel = true;
    };
  }, [onFetchPublicLandingEventFeed]);

  useEffect(() => {
    if (!USE_OPTIMIZED_LANDING_EVENT_FEEDS || !isAuthenticated) {
      if (!isAuthenticated) {
        setLocationPreferences(null);
        setUserHasSavedInterests(null);
        setUserInterestTerms([]);
        setNearYouEvents([]);
        setCsvEventsForYou([]);
        setIsLoadingNearYouEvents(false);
        setIsLoadingCsvEventsForYou(false);
      }
      return undefined;
    }
    if (!currentUser?.id?.uuid || !onFetchPersonalizedLandingEventFeed) {
      return undefined;
    }

    let didCancel = false;
    const viewerKey = currentUser.id.uuid;
    setIsLoadingNearYouEvents(true);
    setIsLoadingCsvEventsForYou(true);

    onFetchPersonalizedLandingEventFeed(viewerKey)
      .then(payload => {
        if (didCancel) return;
        const preferences = payload?.preferences || {};
        const interestTerms = extractInterestTerms(preferences);
        const nearYou = Array.isArray(payload?.sections?.nearYou?.events)
          ? payload.sections.nearYou.events.map(event =>
              formatLandingEventForCard(event, ['Near you'])
            )
          : [];
        const forYou = Array.isArray(payload?.sections?.forYou?.events)
          ? payload.sections.forYou.events.map(event =>
              formatLandingEventForCard(event, event.matchReasons)
            )
          : [];

        setLocationPreferences(preferences.location_preferences || null);
        setUserHasSavedInterests(Boolean(payload?.hasPreferences));
        setUserInterestTerms(interestTerms);
        setNearYouEvents(nearYou);
        setCsvEventsForYou(forYou);
        setIsLoadingNearYouEvents(false);
        setIsLoadingCsvEventsForYou(false);
      })
      .catch(error => {
        if (didCancel) return;
        console.error('[CustomLandingPage] Failed to fetch personalized landing feed:', error);
        setNearYouEvents([]);
        setCsvEventsForYou([]);
        setIsLoadingNearYouEvents(false);
        setIsLoadingCsvEventsForYou(false);
      });

    return () => {
      didCancel = true;
      onCancelPersonalizedLandingEventFeed?.(viewerKey);
    };
  }, [
    currentUser?.id?.uuid,
    isAuthenticated,
    onCancelPersonalizedLandingEventFeed,
    onFetchPersonalizedLandingEventFeed,
  ]);

  useEffect(() => {
    const cleanup = scheduleDeferredLandingTask(
      () => {
        setDeferredLandingReady(true);
      },
      { timeout: 350, delay: 80 }
    );

    return cleanup;
  }, []);

  useEffect(() => {
    if (USE_BATCH_LANDING_EVENTS) return undefined;
    if (!deferredLandingReady) {
      return undefined;
    }

    return scheduleDeferredLandingTask(
      () => {
        setSecondaryLandingReady(true);
      },
      { timeout: 900, delay: 180 }
    );
  }, [deferredLandingReady]);

  useEffect(() => {
    if (!secondaryLandingReady) {
      return undefined;
    }

    return scheduleDeferredLandingTask(
      () => {
        setHeavyLandingReady(true);
      },
      { timeout: 1800, delay: 450 }
    );
  }, [secondaryLandingReady]);

  useEffect(() => {
    if (!isAuthenticated || showCrewHighlightsSection) {
      return;
    }

    return observeLandingSection(crewHighlightsSectionRef.current, () => {
      setShowCrewHighlightsSection(true);
    });
  }, [isAuthenticated, showCrewHighlightsSection]);

  useEffect(() => {
    if (isAuthenticated || showLoggedOutLinkUpsSection) {
      return;
    }

    return observeLandingSection(loggedOutLinkUpsSectionRef.current, () => {
      setShowLoggedOutLinkUpsSection(true);
    });
  }, [isAuthenticated, showLoggedOutLinkUpsSection]);

  // Fetch user crews when user logs in. Crew highlight data is loaded separately
  // with per-crew caching so the landing page doesn't prefetch every bid/linkup.
  useEffect(() => {
    if (
      !secondaryLandingReady ||
      !isAuthenticated ||
      !currentUser ||
      !currentUser.id ||
      hasBootstrappedAuthenticatedData.current
    ) {
      return;
    }

    hasBootstrappedAuthenticatedData.current = true;

    onFetchUserCrews().catch(error => {
      console.error('[CustomLandingPage] Error fetching crew data:', error);
    });
  }, [secondaryLandingReady, isAuthenticated, currentUser?.id, onFetchUserCrews]);

  useEffect(() => {
    if (!secondaryLandingReady || !isAuthenticated || !onFetchFeaturedCrews) {
      return;
    }

    if (featuredCrews.length === 0 && !isFetchingCrewsData) {
      onFetchFeaturedCrews({ sortBy: 'recent', limit: LANDING_FEATURED_CREWS_LIMIT }).catch(err =>
        console.error('[CustomLandingPage] Failed to fetch featured crews for discovery:', err)
      );
    }
  }, [
    secondaryLandingReady,
    featuredCrews.length,
    isAuthenticated,
    isFetchingCrewsData,
    onFetchFeaturedCrews,
  ]);

  useEffect(() => {
    if (!secondaryLandingReady || !isAuthenticated || !onFetchBrowseBids || !config) {
      return;
    }

    onFetchBrowseBids({ page: 1, perPage: 24 }, config).catch(err =>
      console.error('[CustomLandingPage] Failed to fetch browse ticket bids:', err)
    );
  }, [config, secondaryLandingReady, isAuthenticated, onFetchBrowseBids]);

  useEffect(() => {
    if (USE_OPTIMIZED_LANDING_EVENT_FEEDS) return undefined;
    if (!isAuthenticated || !currentUser?.id?.uuid) {
      setLocationPreferences(null);
      setUserHasSavedInterests(null);
      setUserInterestTerms([]);
      return;
    }

    let didCancel = false;
    getUserPreferences(currentUser.id.uuid)
      .then(response => {
        if (didCancel) {
          return;
        }

        const preferences = response?.preferences || {};
        setLocationPreferences(preferences.location_preferences || null);
        setUserHasSavedInterests(hasSavedInterestSelections(preferences));
        setUserInterestTerms(extractInterestTerms(preferences));
      })
      .catch(error => {
        if (!didCancel) {
          console.error('[CustomLandingPage] Failed to fetch user preferences:', error);
          setLocationPreferences(null);
          setUserHasSavedInterests(null);
          setUserInterestTerms([]);
        }
      });

    return () => {
      didCancel = true;
    };
  }, [currentUser?.id?.uuid, isAuthenticated]);

  // Fetch primary logged-out crew data after the shell is interactive.
  useEffect(() => {
    if (!secondaryLandingReady || isAuthenticated || hasBootstrappedGuestData.current) return;

    hasBootstrappedGuestData.current = true;

    // Fetch featured crews (same as CrewsPage)
    if (onFetchFeaturedCrews && featuredCrews.length === 0 && !isFetchingCrewsData) {
      onFetchFeaturedCrews({ sortBy: 'recent', limit: LANDING_FEATURED_CREWS_LIMIT }).catch(err =>
        console.error('[CustomLandingPage] Failed to fetch featured crews:', err)
      );
    }
  }, [
    featuredCrews.length,
    isAuthenticated,
    isFetchingCrewsData,
    onFetchFeaturedCrews,
    secondaryLandingReady,
  ]);

  useEffect(() => {
    if (
      isAuthenticated ||
      !showLoggedOutLinkUpsSection ||
      !onFetchLinkups ||
      allLinkups.length > 0 ||
      isFetchingLinkupsData
    ) {
      return;
    }

    onFetchLinkups({ limit: LANDING_LINKUPS_LIMIT }).catch(err =>
      console.error('[CustomLandingPage] Failed to fetch linkups:', err)
    );
  }, [
    allLinkups.length,
    isAuthenticated,
    isFetchingLinkupsData,
    onFetchLinkups,
    showLoggedOutLinkUpsSection,
  ]);

  useEffect(() => {
    if (USE_OPTIMIZED_LANDING_EVENT_FEEDS) return undefined;
    if (USE_BATCH_LANDING_EVENTS) return undefined;
    if (!deferredLandingReady) {
      return undefined;
    }

    const hasCachedCategoryEvents =
      categoryEventsCache.data &&
      Object.values(categoryEventsCache.data).some(
        events => Array.isArray(events) && events.length > 0
      );
    if (hasCachedCategoryEvents && hasFreshLandingCache(categoryEventsCache.fetchedAt)) {
      setCategoryEvents(categoryEventsCache.data);
      setIsLoadingCategoryEvents(false);
      setLoadingCategoryEventsByKey({
        music: false,
        sports: false,
        comedy: false,
        theater: false,
      });
      return undefined;
    }

    let didCancel = false;
    setIsLoadingCategoryEvents(true);
    setLoadingCategoryEventsByKey({
      music: true,
      sports: true,
      comedy: true,
      theater: true,
    });
    setCategoryEvents({ music: [], sports: [], comedy: [], theater: [] });

    const requestPromises = CATEGORY_SECTION_CONFIG.map(section =>
      getCsvDiscoveryEvents({
        category: section.key,
        limit: LANDING_SECTION_EVENT_LIMIT,
      })
        .then(response => ({
          key: section.key,
          events: Array.isArray(response?.events)
            ? response.events.map(event => formatLandingEventForCard(event, [section.title]))
            : [],
        }))
        .catch(error => {
          console.error(
            `[CustomLandingPage] Failed to fetch ${section.key} category events:`,
            error
          );
          return { key: section.key, events: [] };
        })
        .then(result => {
          if (!didCancel) {
            // Publish this rail immediately. Faster categories should not wait
            // for the slowest discovery request before replacing skeletons.
            setCategoryEvents(current => ({
              ...current,
              [result.key]: result.events,
            }));
            setLoadingCategoryEventsByKey(current => ({
              ...current,
              [result.key]: false,
            }));
          }
          return result;
        })
    );

    Promise.all(requestPromises).then(results => {
      if (didCancel) {
        return;
      }

      const nextCategoryEvents = results.reduce(
        (acc, result) => ({ ...acc, [result.key]: result.events }),
        { music: [], sports: [], comedy: [], theater: [] }
      );

      if (Object.values(nextCategoryEvents).some(events => events.length > 0)) {
        categoryEventsCache.data = nextCategoryEvents;
        categoryEventsCache.fetchedAt = Date.now();
      }
      setCategoryEvents(nextCategoryEvents);
      setIsLoadingCategoryEvents(false);
    });

    return () => {
      didCancel = true;
    };
  }, [deferredLandingReady]);

  useEffect(() => {
    if (USE_OPTIMIZED_LANDING_EVENT_FEEDS) return undefined;
    if (USE_BATCH_LANDING_EVENTS) return undefined;
    // Resolve stateCode from primary_state, falling back to deriving it from marketId
    const primaryCity =
      locationPreferences?.primary_city ||
      locationPreferences?.primaryCity ||
      locationPreferences?.city ||
      null;
    const primaryState =
      locationPreferences?.primary_state ||
      locationPreferences?.primaryState ||
      locationPreferences?.state ||
      null;
    const marketId = locationPreferences?.marketId ? String(locationPreferences.marketId) : null;
    const derivedStateCode =
      getStateCode(primaryState) ||
      primaryState ||
      (marketId ? TICKETMASTER_MARKETS.find(m => m.marketId === marketId)?.primaryState : null) ||
      null;

    if (!deferredLandingReady || !isAuthenticated) {
      setNearYouEvents([]);
      setIsLoadingNearYouEvents(false);
      return undefined;
    }

    const cacheKey = `${primaryCity || ''}|${marketId || ''}|${derivedStateCode ||
      ''}`.toUpperCase();
    const cachedEvents = nearYouEventsCache.get(cacheKey);

    if (
      cachedEvents &&
      Array.isArray(cachedEvents.data) &&
      cachedEvents.data.length > 0 &&
      hasFreshLandingCache(cachedEvents.fetchedAt)
    ) {
      setNearYouEvents(cachedEvents.data);
      setIsLoadingNearYouEvents(false);
      return undefined;
    }

    let didCancel = false;
    setIsLoadingNearYouEvents(true);

    getCsvDiscoveryEventsSafely({
      ...(primaryCity ? { city: primaryCity } : {}),
      ...(derivedStateCode ? { state: derivedStateCode } : {}),
      limit: LANDING_SECTION_EVENT_LIMIT,
      requirePrice: true,
    })
      .then(response => {
        if (hasEvents(response)) {
          return response;
        }

        if (derivedStateCode) {
          return getCsvDiscoveryEventsSafely({
            state: derivedStateCode,
            limit: LANDING_SECTION_EVENT_LIMIT,
            requirePrice: true,
          });
        }

        return response;
      })
      .then(response => {
        if (hasEvents(response)) {
          return response;
        }

        return getCsvDiscoveryEventsSafely({
          limit: LANDING_SECTION_EVENT_LIMIT,
          requirePrice: true,
        });
      })
      .then(response => {
        if (hasEvents(response)) {
          return response;
        }

        // Final non-strict fallback: mirror the category rails so the section
        // always populates even when priced CSV data is sparse.
        return getCsvDiscoveryEventsSafely({
          limit: LANDING_SECTION_EVENT_LIMIT,
        });
      })
      .then(response => {
        if (didCancel) {
          return;
        }

        const formattedEvents = Array.isArray(response?.events)
          ? response.events.map(event => formatLandingEventForCard(event, ['Near you']))
          : [];

        if (formattedEvents.length > 0) {
          nearYouEventsCache.set(cacheKey, {
            data: formattedEvents,
            fetchedAt: Date.now(),
          });
        }
        setNearYouEvents(formattedEvents);
        setIsLoadingNearYouEvents(false);
      })
      .catch(error => {
        if (!didCancel) {
          console.error('[CustomLandingPage] Failed to fetch events near you:', error);
          setNearYouEvents([]);
          setIsLoadingNearYouEvents(false);
        }
      });

    return () => {
      didCancel = true;
    };
  }, [
    deferredLandingReady,
    isAuthenticated,
    locationPreferences?.primary_city,
    locationPreferences?.primaryCity,
    locationPreferences?.city,
    locationPreferences?.primary_state,
    locationPreferences?.primaryState,
    locationPreferences?.state,
    locationPreferences?.marketId,
  ]);

  useEffect(() => {
    if (USE_OPTIMIZED_LANDING_EVENT_FEEDS) return undefined;
    if (USE_BATCH_LANDING_EVENTS) return undefined;
    if (!deferredLandingReady || !isAuthenticated) {
      setCsvEventsForYou([]);
      setIsLoadingCsvEventsForYou(false);
      return undefined;
    }

    const viewerKey = currentUser?.id?.uuid || 'authenticated';
    const eventsForYouCacheKey = `${viewerKey}|${userInterestTerms.join(
      ','
    )}|${locationPreferences?.primary_city ||
      locationPreferences?.primaryCity ||
      locationPreferences?.city ||
      ''}|${locationPreferences?.primary_state ||
      locationPreferences?.primaryState ||
      locationPreferences?.state ||
      ''}`;
    const cachedEventsForYou = eventsForYouCache.get(eventsForYouCacheKey);
    if (
      cachedEventsForYou &&
      Array.isArray(cachedEventsForYou.data) &&
      cachedEventsForYou.data.length > 0 &&
      hasFreshLandingCache(cachedEventsForYou.fetchedAt)
    ) {
      setCsvEventsForYou(cachedEventsForYou.data);
      setIsLoadingCsvEventsForYou(false);
      return undefined;
    }

    let didCancel = false;
    setIsLoadingCsvEventsForYou(true);

    const baseParams = {
      limit: LANDING_SECTION_DISPLAY_LIMIT,
      requireImage: true,
      requirePrice: true,
      ...(userInterestTerms.length ? { interests: userInterestTerms.join(',') } : {}),
    };
    const primaryCity =
      locationPreferences?.primary_city ||
      locationPreferences?.primaryCity ||
      locationPreferences?.city ||
      null;
    const primaryState =
      locationPreferences?.primary_state ||
      locationPreferences?.primaryState ||
      locationPreferences?.state ||
      null;
    const locationParams = {
      ...(primaryCity ? { city: primaryCity } : {}),
      ...(primaryState
        ? {
            state: getStateCode(primaryState) || primaryState,
          }
        : {}),
    };

    getCsvDiscoveryEventsSafely({
      ...baseParams,
      ...locationParams,
    })
      .then(response => {
        if (Array.isArray(response?.events) && response.events.length > 0) {
          return response;
        }

        return getCsvDiscoveryEventsSafely(baseParams);
      })
      .then(response => {
        if (Array.isArray(response?.events) && response.events.length > 0) {
          return response;
        }

        return getCsvDiscoveryEventsSafely({
          ...(userInterestTerms.length ? { interests: userInterestTerms.join(',') } : {}),
          limit: LANDING_SECTION_DISPLAY_LIMIT,
          requirePrice: true,
        });
      })
      .then(response => {
        if (Array.isArray(response?.events) && response.events.length > 0) {
          return response;
        }

        return getCsvDiscoveryEventsSafely({
          limit: LANDING_SECTION_DISPLAY_LIMIT,
          requirePrice: true,
        });
      })
      .then(response => {
        if (Array.isArray(response?.events) && response.events.length > 0) {
          return response;
        }

        // Final non-strict fallback: mirror the category rails so the section
        // always populates even when priced/imaged CSV data is sparse.
        return getCsvDiscoveryEventsSafely({
          ...(userInterestTerms.length ? { interests: userInterestTerms.join(',') } : {}),
          limit: LANDING_SECTION_DISPLAY_LIMIT,
        });
      })
      .then(response => {
        if (didCancel) {
          return;
        }

        const formattedEvents = Array.isArray(response?.events)
          ? response.events.map(event => formatLandingEventForCard(event, event.matchReasons))
          : [];
        if (formattedEvents.length > 0) {
          eventsForYouCache.set(eventsForYouCacheKey, {
            data: formattedEvents,
            fetchedAt: Date.now(),
          });
        }
        setCsvEventsForYou(formattedEvents);
        setIsLoadingCsvEventsForYou(false);
      })
      .catch(error => {
        if (!didCancel) {
          console.error('[CustomLandingPage] Failed to fetch CSV events for you:', error);
          setCsvEventsForYou([]);
          setIsLoadingCsvEventsForYou(false);
        }
      });

    return () => {
      didCancel = true;
    };
  }, [
    currentUser?.id?.uuid,
    deferredLandingReady,
    isAuthenticated,
    locationPreferences?.primary_city,
    locationPreferences?.primaryCity,
    locationPreferences?.city,
    locationPreferences?.primary_state,
    locationPreferences?.primaryState,
    locationPreferences?.state,
    userInterestTerms,
  ]);

  const landingEventSectionsRef = useRef(landingEventSections);
  landingEventSectionsRef.current = landingEventSections;

  useEffect(() => {
    if (!USE_BATCH_LANDING_EVENTS) return undefined;

    const primaryCity =
      locationPreferences?.primary_city ||
      locationPreferences?.primaryCity ||
      locationPreferences?.city ||
      null;
    const primaryState =
      locationPreferences?.primary_state ||
      locationPreferences?.primaryState ||
      locationPreferences?.state ||
      null;
    const state = primaryState ? getStateCode(primaryState) || primaryState : null;
    const interests = userInterestTerms.length ? userInterestTerms.join(',') : null;
    let cancelled = false;

    setIsLoadingCategoryEvents(true);
    setIsLoadingNearYouEvents(Boolean(isAuthenticated));
    setIsLoadingCsvEventsForYou(Boolean(isAuthenticated));

    const sections = [
      ...CATEGORY_SECTION_CONFIG.map(section => ({
        key: section.key,
        category: section.key,
        limit: LANDING_SECTION_EVENT_LIMIT,
      })),
      {
        key: 'nearYou',
        city: isAuthenticated ? primaryCity : undefined,
        state: isAuthenticated ? state : undefined,
        limit: LANDING_SECTION_EVENT_LIMIT,
        requirePrice: true,
      },
      {
        key: 'forYou',
        ...(isAuthenticated && primaryCity ? { city: primaryCity } : {}),
        ...(isAuthenticated && state ? { state } : {}),
        ...(isAuthenticated && interests ? { interests } : {}),
        limit: LANDING_SECTION_DISPLAY_LIMIT,
        requireImage: true,
        requirePrice: true,
      },
    ];

    const cachedSections = landingEventSectionsRef.current;
    const hasCachedData =
      cachedSections &&
      sections.every(section => Array.isArray(cachedSections[section.key]?.events)) &&
      sections.some(section => cachedSections[section.key].events.length > 0);

    (hasCachedData
      ? Promise.resolve(cachedSections)
      : onFetchLandingEventsBatch
      ? onFetchLandingEventsBatch(sections, currentUser?.id?.uuid || 'guest')
      : getCsvBatchDiscoveryEvents(sections).then(res => res?.sections || {})
    )
      .then(data => {
        if (cancelled) return;
        const format = (key, reasons) =>
          Array.isArray(data[key]?.events)
            ? data[key].events.map(event =>
                formatLandingEventForCard(event, reasons || event.matchReasons)
              )
            : [];
        const nextCategories = CATEGORY_SECTION_CONFIG.reduce(
          (acc, section) => ({ ...acc, [section.key]: format(section.key, [section.title]) }),
          { music: [], sports: [], comedy: [], theater: [] }
        );
        setCategoryEvents(nextCategories);
        setNearYouEvents(format('nearYou', ['Near you']));
        setCsvEventsForYou(format('forYou'));
      })
      .catch(error => {
        if (cancelled) return;
        console.error('[CustomLandingPage] Failed to fetch landing event batch:', error);
        setCategoryEvents({ music: [], sports: [], comedy: [], theater: [] });
        setNearYouEvents([]);
        setCsvEventsForYou([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingCategoryEvents(false);
        setIsLoadingNearYouEvents(false);
        setIsLoadingCsvEventsForYou(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    locationPreferences?.primary_city,
    locationPreferences?.primaryCity,
    locationPreferences?.city,
    locationPreferences?.primary_state,
    locationPreferences?.primaryState,
    locationPreferences?.state,
    userInterestTerms,
    onFetchLandingEventsBatch,
    currentUser?.id?.uuid,
  ]);

  useEffect(() => {
    if (!heavyLandingReady || !isAuthenticated || !onFetchVerifiedListings) {
      setVerifiedListings([]);
      setIsLoadingVerifiedListings(false);
      return undefined;
    }

    let didCancel = false;
    setIsLoadingVerifiedListings(true);

    onFetchVerifiedListings({ limit: LANDING_SECTION_LISTING_LIMIT })
      .then(listings => {
        if (didCancel) {
          return;
        }

        const mappedListings = Array.isArray(listings)
          ? listings.map(mapSharetribeListingToCard)
          : [];
        setVerifiedListings(mappedListings);
        setIsLoadingVerifiedListings(false);
      })
      .catch(error => {
        if (!didCancel) {
          console.error('[CustomLandingPage] Failed to fetch verified listings section:', error);
          setVerifiedListings([]);
          setIsLoadingVerifiedListings(false);
        }
      });

    return () => {
      didCancel = true;
    };
  }, [heavyLandingReady, isAuthenticated, onFetchVerifiedListings]);

  // Prepare events for display - the API returns recommendations as an array, not sections.topPicks
  // Use recommendations array directly if sections.topPicks doesn't exist
  const rawTopPicksEvents =
    recommendationData?.sections?.topPicks || recommendationData?.recommendations || [];

  const topPicksEvents = useMemo(() => {
    const filteredTopPicksEvents = rawTopPicksEvents.filter(event => {
      const hasLocationName =
        event?.location?.name &&
        typeof event.location.name === 'string' &&
        event.location.name.trim().length > 0 &&
        event.location.name !== 'Venue TBA' &&
        event.location.name.toLowerCase() !== 'venue tba';

      const hasVenue =
        event?.venue &&
        typeof event.venue === 'string' &&
        event.venue.trim().length > 0 &&
        event.venue !== 'Venue TBA';

      const hasVenueName =
        event?.venueName &&
        typeof event.venueName === 'string' &&
        event.venueName.trim().length > 0 &&
        event.venueName !== 'Venue TBA';

      const hasLocationData =
        event?.location &&
        ((event.location.city &&
          typeof event.location.city === 'string' &&
          event.location.city.trim().length > 0) ||
          (event.location.stateCode &&
            typeof event.location.stateCode === 'string' &&
            event.location.stateCode.trim().length > 0) ||
          (event.location.state &&
            typeof event.location.state === 'string' &&
            event.location.state.trim().length > 0));

      return hasLocationName || hasVenue || hasVenueName || hasLocationData;
    });

    const eventsToFormat =
      filteredTopPicksEvents.length > 0 ? filteredTopPicksEvents : rawTopPicksEvents;

    return eventsToFormat
      .map(e => formatLandingEventForCard(e, e.matchReasons))
      .slice(0, LANDING_SECTION_DISPLAY_LIMIT);
  }, [rawTopPicksEvents]);

  const eventsForYou = isAuthenticated ? csvEventsForYou : topPicksEvents;
  const hasKnownInterestState = !isAuthenticated || userHasSavedInterests !== null;
  const hasEventsForYouPreferences =
    userHasSavedInterests !== null ? userHasSavedInterests : hasRecommendationPreferences;
  const shouldShowEventsForYouLoading = isAuthenticated && isLoadingCsvEventsForYou;
  const shouldShowNoMatchingEvents = false;

  const guestEventsForYou = useMemo(
    () =>
      CATEGORY_SECTION_CONFIG.flatMap(section =>
        (categoryEvents[section.key] || [])
          .slice(0, LANDING_GUEST_EVENTS_PER_CATEGORY)
          .map(event => ({
            ...event,
            category: event.category || section.title,
          }))
      ).slice(0, LANDING_SECTION_DISPLAY_LIMIT),
    [categoryEvents]
  );

  // Handler for logged-out LinkUp card click -> open modal
  const handleLoggedOutLinkUpClick = linkUpId => {
    const linkUp = transformedLinkups.find(l => l.id === linkUpId);
    if (linkUp) {
      setSelectedLinkUp(linkUp);
      setShowLinkUpModal(true);
    }
  };

  // Navigation handlers for crew highlights
  // Navigate to the same bid details (ListingPage) as from BidsPage
  const handleBidClick = async (bidId, crewId, bidData = null) => {
    const bid = bidData || null;
    const listing = bid?.listing || null;
    let listingId =
      listing?.id?.uuid || listing?.id?.id?.uuid || listing?.id || bid?.listingId || null;
    let listingTitle = listing?.attributes?.title || bid?.listingTitle || bid?.title || 'Bid';

    if (!listing) {
      try {
        const resolved = await resolveBidListingApi(bidId);
        listingId = resolved?.listingId || listingId;
        listingTitle = resolved?.title || listingTitle;
      } catch (e) {
        console.warn('[CustomLandingPage] Failed to resolve clicked bid listing', {
          bidId,
          crewId,
          listingId,
          message: e?.message || 'unknown error',
        });
      }
    }

    if (!listingId) {
      history.push('/ticket-bids');
      return;
    }

    const slug = createSlug(listingTitle);

    const path = createResourceLocatorString(
      'ListingPage',
      routeConfiguration,
      { id: listingId, slug },
      {}
    );

    history.push(path);
  };

  // Find the raw LinkUp object from highlightsByCrewId for a given crew + linkup id
  const findHighlightedLinkUp = (linkupId, crewId) => {
    const crewHighlights = highlightsByCrewId[String(crewId)] || {};
    const raw = crewHighlights.linkup;
    if (!raw) return null;
    const rawId = String(raw.id);
    if (rawId !== String(linkupId)) return null;

    // Map to LinkUpDetailsModal expected shape
    return {
      id: rawId,
      title: raw.title || raw.name || raw.linkUp_name || 'LinkUp',
      location:
        raw.location || raw.locationDescription || raw.linkUp_address || raw.city || 'Location TBD',
      linkUp_address: raw.linkUp_address || '',
      city: deriveCityFromAddress(raw.linkUp_address) || raw.city || '',
      description: raw.description || raw.linkUp_description || '',
      image: raw.image || raw.linkUp_header_image || raw.image_url || raw.event?.image_url || null,
      date: raw.date || raw.linkUp_date || raw.event_date || '',
      time: raw.time || raw.linkUp_time || '',
      timezone: raw.timezone || raw.linkUp_timezone || '',
      totalGoing: raw.totalGoing || raw.total_going || raw.attendee_count || 0,
    };
  };

  // Open LinkUp details modal from Crew Highlights
  const handleLinkupClick = (linkupId, crewId) => {
    const linkUp = findHighlightedLinkUp(linkupId, crewId);
    if (!linkUp) {
      // Fallback: navigate directly if we can't resolve the highlight
      history.push(`/linkup-chat/${linkupId}`);
      return;
    }
    setSelectedLinkUp(linkUp);
    setShowLinkUpModal(true);
  };

  const handleCloseLinkUpModal = () => {
    setShowLinkUpModal(false);
    setSelectedLinkUp(null);
  };

  const handleJoinLinkUpFromModal = async linkUpId => {
    if (!user?.id?.uuid) {
      // If not authenticated, send them to login; LinkUp join requires auth.
      history.push('/login');
      return;
    }
    try {
      await onJoinLinkUp(linkUpId, user.id.uuid);
    } catch (e) {
      // Errors are already logged in the duck; no extra handling needed here.
    }
  };

  const handlePostClick = (postId, crewId) => {
    // Navigate to crew page or post detail
    history.push(`/crews/${crewId}`);
  };

  const joinedCrewIds = useMemo(() => userCrews.map(crew => String(crew.id)), [userCrews]);

  const discoverCrews = useMemo(
    () =>
      (featuredCrews || [])
        .filter(crew => !joinedCrewIds.includes(String(crew.id)))
        .slice(0, LANDING_SECTION_DISPLAY_LIMIT),
    [featuredCrews, joinedCrewIds]
  );

  const loggedOutCrews = useMemo(
    () =>
      (featuredCrews.length > 0 ? featuredCrews : mockCrews).slice(
        0,
        LANDING_SECTION_DISPLAY_LIMIT
      ),
    [featuredCrews]
  );

  const loggedOutLinkups = useMemo(
    () => transformedLinkups.slice(0, LANDING_SECTION_DISPLAY_LIMIT),
    [transformedLinkups]
  );

  const landingTicketBids = useMemo(
    () =>
      (bidListings || [])
        .map(formatBrowseListingForBidCard)
        .filter(bid => bid.status === 'active')
        .slice(0, LANDING_SECTION_BIDS_LIMIT),
    [bidListings]
  );

  const title = seoMetaProps.title || intl.formatMessage({ id: 'CustomLandingPage.title' });
  const description = seoMetaProps.description;
  const socialSharing = seoMetaProps.socialSharing || {
    title,
    ...(description ? { description } : {}),
  };

  return (
    <Page
      title={title}
      description={description}
      socialSharing={socialSharing}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {/* Hero Search Section */}
          <HeroSearchSection
            onSearch={handleSearch}
            onEventSelect={handleEventClick}
            onAttractionSelect={handleAttractionClick}
          />

          <div className={css.content}>
            {isAuthenticated ? (
              <>
                {(shouldShowEventsForYouLoading || eventsForYou.length > 0) && (
                  <EventsSection
                    title="Events for you"
                    events={eventsForYou.length > 0 ? eventsForYou : []}
                    onEventClick={handleEventClick}
                    user={user}
                    showScrollControls={true}
                    scrollRef={eventsForYouRef}
                    keyPrefix="events-for-you"
                    iconSrc={recommendationsIcon}
                    iconAlt="Recommended events"
                    iconSize={39}
                    loading={shouldShowEventsForYouLoading}
                  />
                )}

                {!recommendationsLoading && hasKnownInterestState && !hasEventsForYouPreferences && (
                  <div className={css.emptyState}>
                    <div className={css.emptyStateContent}>
                      <h2 className={css.emptyStateTitle}>Let's personalize your experience!</h2>
                      <p className={css.emptyStateDescription}>
                        Set your preferences and we'll tailor your feed just for you.
                      </p>
                      <PrimaryButton
                        onClick={() =>
                          history.push('/user-settings?section=social&subsection=my-interests')
                        }
                        className={css.setInterestsButton}
                      >
                        Set your interests
                      </PrimaryButton>
                    </div>
                  </div>
                )}

                {shouldShowNoMatchingEvents && (
                  <div className={css.emptyState}>
                    <div className={css.emptyStateContent}>
                      <h2 className={css.emptyStateTitle}>No matching events right now</h2>
                      <p className={css.emptyStateDescription}>
                        We couldn&apos;t find any upcoming events for your current interests yet.
                        Try updating your interests or check back soon.
                      </p>
                      <PrimaryButton
                        onClick={() =>
                          history.push('/user-settings?section=social&subsection=my-interests')
                        }
                        className={css.setInterestsButton}
                      >
                        Update your interests
                      </PrimaryButton>
                    </div>
                  </div>
                )}

                <div ref={crewHighlightsSectionRef} className={css.deferredSection}>
                  {showCrewHighlightsSection ? (
                    <CrewHighlightsSection
                      title="Crew highlights"
                      userCrews={userCrews}
                      highlightsByCrewId={highlightsByCrewId}
                      fetchInProgress={fetchInProgress}
                      fetchError={fetchError}
                      onFetchHighlights={onFetchCrewHighlights}
                      onBidClick={handleBidClick}
                      onLinkupClick={handleLinkupClick}
                      onPostClick={handlePostClick}
                      user={user}
                      showScrollControls={true}
                      scrollRef={crewHighlightsRef}
                      iconSrc={highlightsIcon}
                      iconAlt="Crew highlights"
                      iconSize={39}
                    />
                  ) : (
                    <div className={css.loadingContainer}>
                      <p>Preparing Crew highlights...</p>
                    </div>
                  )}
                </div>

                {(isLoadingNearYouEvents || meetsMinimumSectionItems(nearYouEvents)) && (
                  <EventsSection
                    title="Events near you"
                    events={nearYouEvents}
                    onEventClick={handleEventClick}
                    user={user}
                    showScrollControls={true}
                    scrollRef={eventsNearYouRef}
                    keyPrefix="events-near-you"
                    loading={isLoadingNearYouEvents}
                    iconSrc={locationIcon}
                    iconAlt="Events near you"
                    iconSize={39}
                  />
                )}

                {CATEGORY_SECTION_CONFIG.map(section =>
                  loadingCategoryEventsByKey[section.key] ||
                  (Array.isArray(categoryEvents[section.key]) &&
                    categoryEvents[section.key].length > 0) ? (
                    <EventsSection
                      key={`auth-category-${section.key}`}
                      title={section.title}
                      events={categoryEvents[section.key]}
                      onEventClick={handleEventClick}
                      user={user}
                      showScrollControls={true}
                      scrollRef={categorySectionRefs.current[section.key]}
                      keyPrefix={`auth-category-${section.key}`}
                      loading={Boolean(loadingCategoryEventsByKey[section.key])}
                      iconSrc={section.icon}
                      iconAlt={section.title}
                      iconAsMask={true}
                    />
                  ) : null
                )}

                {(isLoadingVerifiedListings ||
                  (Array.isArray(verifiedListings) && verifiedListings.length > 0)) && (
                  <ListingsSection
                    title="Riticket verified listings"
                    listings={verifiedListings}
                    user={user}
                    showScrollControls={true}
                    scrollRef={verifiedListingsRef}
                    keyPrefix="verified-listings"
                    loading={isLoadingVerifiedListings}
                    iconSrc={verifiedIcon}
                    iconAlt="Verified listings"
                    iconSize={39}
                  />
                )}

                {(browseBidsLoading || landingTicketBids.length > 0) && (
                  <section className={css.linkUpsSection}>
                    <SectionHeader
                      title="Ticket bids"
                      user={user}
                      iconSrc={bidsIcon}
                      iconAlt="Ticket bids"
                    />
                    <div className={css.scrollControlsContainer}>
                      <ScrollControls containerRef={ticketBidsRef} theme="dark" />
                    </div>
                    {browseBidsLoading && landingTicketBids.length === 0 ? (
                      <div className={css.loadingContainer}>
                        <p>Getting bids...</p>
                      </div>
                    ) : (
                      <div ref={ticketBidsRef} className={css.linkUpsGrid}>
                        {landingTicketBids.map(bid => (
                          <BidCard
                            key={`landing-ticket-bid-${bid.id}`}
                            bid={bid}
                            theme="dark"
                            makeOfferButtonClassName={css.ticketBidOfferButton}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {(isFetchingCrewsData || meetsMinimumSectionItems(discoverCrews)) && (
                  <CrewsSection
                    title="Crews"
                    catchyPhrase="Your vibe, your tribe."
                    description="Find groups where fans become besties."
                    crews={discoverCrews}
                    onCrewClick={handleCrewClick}
                    onJoinCrew={handleJoinCrew}
                    joinedCrews={joinedCrewIds}
                    user={user}
                    showScrollControls={true}
                    scrollRef={discoverCrewsRef}
                    keyPrefix="discover-crews"
                    loading={isFetchingCrewsData && discoverCrews.length === 0}
                    iconSrc={crewsIcon}
                    iconAlt="Crews"
                    cardTheme="dark"
                    joinCrewButtonClassName={css.landingCrewJoinButton}
                  />
                )}
              </>
            ) : (
              <>
                {(isLoadingCategoryEvents || meetsMinimumSectionItems(guestEventsForYou)) && (
                  <EventsSection
                    title="Events for you"
                    events={guestEventsForYou}
                    onEventClick={handleEventClick}
                    user={user}
                    showScrollControls={true}
                    scrollRef={eventsForYouRef}
                    keyPrefix="guest-events-for-you"
                    iconSrc={recommendationsIcon}
                    iconAlt="Recommended events"
                    iconSize={39}
                    loading={isLoadingCategoryEvents && guestEventsForYou.length === 0}
                  />
                )}

                <CrewsSection
                  title="Crews"
                  catchyPhrase="Your vibe, your tribe."
                  description="Find groups where fans become besties."
                  crews={loggedOutCrews}
                  onCrewClick={handleCrewClick}
                  onJoinCrew={handleJoinCrew}
                  joinedCrews={[]}
                  user={user}
                  showScrollControls={true}
                  scrollRef={crewsRef}
                  keyPrefix="crews"
                  loading={isFetchingCrewsData && featuredCrews.length === 0}
                  iconSrc={crewsIcon}
                  iconAlt="Crews"
                  cardTheme="dark"
                  joinCrewButtonClassName={css.landingCrewJoinButton}
                />

                {(isFetchingLinkupsData || loggedOutLinkups.length > 0) && (
                  <div ref={loggedOutLinkUpsSectionRef} className={css.deferredSection}>
                    {showLoggedOutLinkUpsSection ? (
                      <section className={css.linkUpsSection}>
                        <SectionHeader
                          title="LinkUps"
                          user={user}
                          iconSrc={linkupsIcon}
                          iconAlt="LinkUps"
                        />
                        <div className={css.linkUpsSubtitle}>
                          <span className={css.catchyPhrase}>Make it a night.</span>
                          <p className={css.sectionDescription}>
                            From pregame to the afterparty, create or join group hangs.
                          </p>
                        </div>
                        <div className={css.scrollControlsContainer}>
                          <ScrollControls containerRef={loggedOutLinkUpsRef} theme="dark" />
                        </div>
                        {isFetchingLinkupsData ? (
                          <div className={css.loadingContainer}>
                            <p>Loading LinkUps...</p>
                          </div>
                        ) : (
                          <div ref={loggedOutLinkUpsRef} className={css.linkUpsGrid}>
                            {loggedOutLinkups.map(linkUp => (
                              <LinkUpCard
                                key={`loggedout-linkup-${linkUp.id}`}
                                linkUp={linkUp}
                                isJoined={false}
                                crewCount={0}
                                onClick={handleLoggedOutLinkUpClick}
                                onButtonClick={() => history.push('/login')}
                                theme="dark"
                              />
                            ))}
                          </div>
                        )}
                      </section>
                    ) : (
                      <div className={css.loadingContainer}>
                        <p>Preparing LinkUps...</p>
                      </div>
                    )}
                  </div>
                )}

                {CATEGORY_SECTION_CONFIG.map(section =>
                  loadingCategoryEventsByKey[section.key] ||
                  (Array.isArray(categoryEvents[section.key]) &&
                    categoryEvents[section.key].length > 0) ? (
                    <EventsSection
                      key={`guest-category-${section.key}`}
                      title={section.title}
                      events={categoryEvents[section.key]}
                      onEventClick={handleEventClick}
                      user={user}
                      showScrollControls={true}
                      scrollRef={categorySectionRefs.current[section.key]}
                      keyPrefix={`guest-category-${section.key}`}
                      loading={Boolean(loadingCategoryEventsByKey[section.key])}
                      iconSrc={section.icon}
                      iconAlt={section.title}
                      iconAsMask={true}
                    />
                  ) : null
                )}
              </>
            )}
          </div>
        </div>
      </LayoutSingleColumn>

      {/* LinkUp Details Modal for Crew Highlights */}
      {showLinkUpModal ? (
        <LinkUpDetailsModal
          isOpen={showLinkUpModal}
          onClose={handleCloseLinkUpModal}
          linkUp={selectedLinkUp}
          joinedLinkUps={joinedLinkUpIds}
          history={history}
          onJoinLinkUp={handleJoinLinkUpFromModal}
        />
      ) : null}

      <Modal
        id="LandingPaymentFailedPreviewModal"
        isOpen={showPaymentFailedPreview}
        onClose={() => setShowPaymentFailedPreview(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={checkoutCss.paymentFailedModalContent}>
          <H3 as="h1" className={checkoutCss.heading}>
            Payment unsuccessful
          </H3>
          <p className={checkoutCss.paymentFailedMessage}>
            Your payment did not go through, and your card was not charged. Please check your card
            details or try again.
          </p>
          <PrimaryButton type="button" onClick={() => setShowPaymentFailedPreview(false)}>
            Try again
          </PrimaryButton>
        </div>
      </Modal>
    </Page>
  );
};

CustomLandingPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  scrollingDisabled: PropTypes.bool.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  isAuthenticated: PropTypes.bool,
  seoMetaProps: PropTypes.object,
};

const mapStateToProps = state => {
  const bidListingRefs = getBidsResultIds(state).map(id => ({ id, type: 'listing' }));
  const bidListings = getMarketplaceEntities(state, bidListingRefs)
    .map(ensureListing)
    .filter(
      listing => listing?.attributes?.publicData?.listingType === LISTING_TYPES.TICKET_REQUEST
    );

  return {
    currentUser: state.user.currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    userCrews: selectRawUserCrews(state),
    bidListings,
    browseBidsLoading: isFetchingBrowseBids(state),
    highlightsByCrewId: selectHighlightsByCrewId(state),
    fetchInProgress: selectCrewHighlightsLoading(state),
    fetchError: selectCrewHighlightsError(state),
    // Logged-out data
    featuredCrews: selectFormattedFeaturedCrews(state),
    isFetchingCrewsData: selectCrewsLoading(state),
    allLinkups: selectRawLinkups(state),
    transformedLinkups: selectLinkupsForCards(state),
    joinedLinkUpIds: selectJoinedLinkUpIds(state).map(String),
    isFetchingLinkupsData: selectLinkupsLoading(state),
    recommendationData: selectAuthenticatedLandingRecommendations(state),
    recommendationsLoading: selectAuthenticatedLandingRecommendationsLoading(state),
    recommendationsError: selectAuthenticatedLandingRecommendationsError(state),
    hasRecommendationPreferences: selectAuthenticatedLandingHasPreferences(state),
    landingEventSections: state.landingEvents?.sections,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchUserCrews: () => dispatch(fetchUserCrews()),
  onFetchCrewHighlights: crewIds => dispatch(fetchCrewHighlights(crewIds)),
  onJoinLinkUp: (linkUpId, userId) => dispatch(joinLinkUp(linkUpId, userId)),
  // Logged-out data fetching
  onFetchFeaturedCrews: options => dispatch(fetchFeaturedCrews(options)),
  onFetchLinkups: options => dispatch(fetchLinkups(options)),
  onFetchBrowseBids: (params, config) => dispatch(fetchBrowseBids(params, config)),
  onFetchVerifiedListings: options => dispatch(fetchVerifiedListings(options)),
  onFetchLandingEventsBatch: (sections, viewerKey) =>
    dispatch(fetchLandingEventsBatch(sections, viewerKey)),
  onFetchPublicLandingEventFeed: () => dispatch(fetchPublicLandingEventFeed()),
  onFetchPersonalizedLandingEventFeed: viewerKey =>
    dispatch(fetchPersonalizedLandingEventFeed(viewerKey, { force: true })),
  onCancelPersonalizedLandingEventFeed: viewerKey =>
    dispatch(cancelPersonalizedLandingEventFeed(viewerKey)),
});

const CustomLandingPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(CustomLandingPageComponent);

export default CustomLandingPage;
